import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { useGpsNotifications } from './useGpsNotifications';
import { useSystemNotifications } from './use-notifications';
import { Bell, Fuel, Gauge, Radio } from 'lucide-react';
import { LucideIcon } from 'lucide-react';

export interface UnifiedNotification {
  id: string;
  type: 'marking' | 'gps' | 'system';
  title: string;
  description: string;
  timestamp: string;
  isRead: boolean;
  priority: 'low' | 'medium' | 'high';
  metadata: Record<string, any>;
  icon: LucideIcon;
  badge?: {
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
    label: string;
  };
}

export interface FleetNotificationsStats {
  total: number;
  unread: number;
  markings: {
    total: number;
    unread: number;
    fuel: number;
    odometer: number;
  };
  gps: {
    total: number;
    failed: number;
    pending: number;
    success: number;
  };
  system: {
    total: number;
    unread: number;
  };
}

// Hook para buscar notificações de marcações
function useMarkingNotifications(companyId?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['marking-notifications', companyId, user?.id],
    queryFn: async () => {
      if (!companyId || !user?.id) return [];
      
      const { data, error } = await supabase
        .from('fleet_marking_notifications')
        .select(`
          *,
          fleet_drivers (
            name,
            cpf
          ),
          fleet_vehicles (
            plate,
            make,
            model
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId && !!user?.id,
  });
}

export function useFleetNotifications(companyId?: string) {
  const { user } = useAuth();
  const markingNotifs = useMarkingNotifications(companyId);
  const gpsNotifs = useGpsNotifications(companyId);
  const systemNotifs = useSystemNotifications();
  
  // Normalizar notificações de marcações
  const normalizedMarkings: UnifiedNotification[] = useMemo(() => 
    (markingNotifs.data || []).map(n => ({
      id: n.id,
      type: 'marking' as const,
      title: `Nova marcação de ${n.marking_type === 'fuel' ? 'Combustível' : 'Odômetro'}`,
      description: `${n.fleet_drivers?.name || 'Motorista'} - ${n.fleet_vehicles?.plate || 'Veículo'}`,
      timestamp: n.created_at,
      isRead: Array.isArray(n.read_by) ? n.read_by.includes(user?.id) : false,
      priority: 'medium' as const,
      metadata: (n.metadata as Record<string, any>) || {},
      icon: n.marking_type === 'fuel' ? Fuel : Gauge,
      badge: {
        variant: 'secondary' as const,
        label: n.marking_type === 'fuel' ? 'Combustível' : 'Odômetro'
      }
    })), [markingNotifs.data, user?.id]
  );
  
  // Normalizar notificações GPS
  const normalizedGps: UnifiedNotification[] = useMemo(() => 
    (gpsNotifs.notifications || []).map(n => ({
      id: n.id,
      type: 'gps' as const,
      title: `Notificação GPS: ${n.event_type}`,
      description: `${n.type} - ${n.status}`,
      timestamp: n.created_at,
      isRead: true, // GPS notifications don't track read status
      priority: n.status === 'failed' ? 'high' as const : 'low' as const,
      metadata: n.payload || {},
      icon: Radio,
      badge: {
        variant: n.status === 'failed' ? 'destructive' : n.status === 'pending' ? 'secondary' : 'default',
        label: n.status === 'success' ? 'Enviado' : n.status === 'failed' ? 'Falhou' : 'Pendente'
      }
    })), [gpsNotifs.notifications]
  );
  
  // Normalizar notificações do sistema
  const normalizedSystem: UnifiedNotification[] = useMemo(() => 
    (systemNotifs.data || []).map(n => ({
      id: n.id,
      type: 'system' as const,
      title: n.title,
      description: n.content,
      timestamp: n.created_at,
      isRead: n.is_read || false,
      priority: n.type === 'warning' ? 'high' : 'low' as const,
      metadata: n.metadata || {},
      icon: Bell,
      badge: {
        variant: n.type === 'warning' ? 'destructive' : 'default',
        label: n.type === 'announcement' ? 'Anúncio' : 'Info'
      }
    })), [systemNotifs.data]
  );
  
  // Combinar todas e ordenar por timestamp
  const allNotifications = useMemo(() => 
    [
      ...normalizedMarkings,
      ...normalizedGps,
      ...normalizedSystem
    ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    [normalizedMarkings, normalizedGps, normalizedSystem]
  );
  
  // Calcular estatísticas
  const stats: FleetNotificationsStats = useMemo(() => ({
    total: allNotifications.length,
    unread: allNotifications.filter(n => !n.isRead).length,
    markings: {
      total: normalizedMarkings.length,
      unread: normalizedMarkings.filter(n => !n.isRead).length,
      fuel: normalizedMarkings.filter(n => n.metadata?.marking_type === 'fuel').length,
      odometer: normalizedMarkings.filter(n => n.metadata?.marking_type === 'odometer').length,
    },
    gps: {
      total: normalizedGps.length,
      failed: gpsNotifs.stats?.failed || 0,
      pending: gpsNotifs.stats?.pending || 0,
      success: gpsNotifs.stats?.success || 0,
    },
    system: {
      total: normalizedSystem.length,
      unread: normalizedSystem.filter(n => !n.isRead).length,
    }
  }), [allNotifications, normalizedMarkings, normalizedGps, gpsNotifs.stats]);
  
  return {
    notifications: allNotifications,
    stats,
    isLoading: markingNotifs.isLoading || gpsNotifs.isLoading || systemNotifs.isLoading,
    
    // Filtros
    filterByType: (type: 'marking' | 'gps' | 'system') => 
      allNotifications.filter(n => n.type === type),
    
    filterUnread: () => 
      allNotifications.filter(n => !n.isRead),
  };
}
